<template>
  <div
    :class="{
      [`is-${pageTheme}-theme`]: pageTheme
    }"
    :style="computedPromoStyles"
  >
    <Html
      :lang="head.htmlAttrs.lang"
    >
      <template
        v-for="link in head.link"
        :key="link.hid"
      >
        <Link
          :id="link.hid"
          :rel="link.rel"
          :href="link.href"
          :hreflang="link.hreflang"
        />
      </template>
      <template
        v-for="meta in head.meta"
        :key="meta.hid"
      >
        <Meta
          :id="meta.hid"
          :property="meta.property"
          :content="meta.content"
        />
      </template>
    </Html>

    <PageLoadingOverlay />
    <ClientOnly>
      <PageMessages />
    </ClientOnly>
    <Navigation
      v-if="!isStandalonePageTheme"
    />
    <VisionSvgSprite />
    
    <ClientOnly>
      <CartSidebar />
    </ClientOnly>

    <div
      :class="$b('main')"
    >
      <slot />
    </div>

    <div :class="$b('footer')">
      <PageFooter />
    </div>
    <DynamicSidebar
      v-if="
        sidebarStore.activeSidebar &&
          sidebarStore.activeSidebar !== 'country-selector'
      "
      :slug="sidebarStore.activeSidebar"
      :visible="
        sidebarStore.activeSidebar !== '' &&
          sidebarStore.activeSidebar !== 'country-selector'
      "
    />
    <LocalisationSidebar
      :visible="sidebarStore.activeSidebar === 'country-selector'"
    />

    <CookieSidebar v-if="pageStore.showCookieModal" />
    <CookieConsentBanner />

    <DynamicModal />

    <ClientOnly>
      <EmailSignupModal
        v-if="
          showSignupModal
            && !hideSignupModal
            && pageStore.cookieStatus !== null
        "
      />
    </ClientOnly>

    <ClientOnly>
      <TaxesAndDutiesModal
        v-if="showTaxesAndDutiesModal && !hideSignupModal"
      />
    </ClientOnly>

    <ClientOnly>
      <LocaleCheckModal v-if="!hideSignupModal && pageStore.cookieStatus !== null" />
    </ClientOnly>
  </div>
</template>

<script setup>
import organisation from '~/assets/js/constants/organisation'
import { setPageThemeFromRoute } from '@/assets/js/helpers/pageThemes'
import { PAGE_THEMES } from '@/assets/js/constants/pageThemes'
import { storeToRefs } from 'pinia';

const route = useRoute()

const isExplorePage = computed(() => {
  return [
    'ExploreListing',
    'Home',
    'locale-explore-category',
    'locale-explore-category-article',
    'ec-news'
  ].includes(route.name)
})

const sidebarStore = useSidebarStore()
const productStore = useProductStore()
const reviewsStore = useReviewsStore()
const pageStore = usePageStore()
const { $sentry, $i18n } = useNuxtApp()
const globalConfigStore = useGlobalConfigStore()
const shopifyStore = useShopifyStore()
const promoStore = usePromoStore()
useLayoutSetup()

const head = ref({})
head.value = $i18n.getLocaleHead()

setPageThemeFromRoute(pageStore, route)
const { pageTheme } = storeToRefs(pageStore)

if (pageTheme !== PAGE_THEMES.explore && isExplorePage.value) {
  pageStore.setPageTheme(PAGE_THEMES.explore)
}
const isStandalonePageTheme = computed(() => {
  return pageTheme.value?.includes('standalone')
})

const showSignupModal = computed(() => {
  return (globalConfigStore.isFlagEnabled('email-signup-modal')
    && !promoStore.theme?.id)
})

const showTaxesAndDutiesModal = computed(() => {
  return (
    $i18n.locale?.value === 'en-us' &&
    globalConfigStore.isFlagEnabled('taxes-and-duties-modal')
  )
})

const hideSignupModal = computed(() => {
  const path = route.path.toLowerCase();
  const pathsToCheck = [
    'tile-turnaround',
    'welcome',
    'competition/',
    'refer-a-friend',
    'the-2024-vision-raise',
    '/returns'
  ];

  if(pathsToCheck.some(pathToCheck => path.includes(pathToCheck))) return true

  return route.query.code || route.query.applyCode
});

try {
  await Promise.all([
    globalConfigStore.getGlobalConfig(),
    productStore.GET_BASE_PRODUCT_DATA(),
    shopifyStore.getAutomaticDiscounts(),
    promoStore.getActiveGlobalPromos(),
    reviewsStore.GET_SUMMARY()
  ])
  await promoStore.checkGlobalPromoIsActive()
} catch (error) {
  $sentry.captureException(error)
}

if (route.query['cart']) shopifyStore.toggleCart(true)

watch(() => route.path, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    head.value = $i18n.getLocaleHead()
  }
})

const computedPromoStyles = computed(() => {
  return promoStore.styles
})

useJsonld(organisation)

useHead({
  bodyAttrs: {
    class: 'sungod'
  }
})
</script>

<script>
export default {
  name: 'DefaultLayout'
}
</script>

<style lang="postcss">
.DefaultLayout__main {
  min-height: calc(75vh - 150px);
}

.DefaultLayout__footer {
  @apply relative;
}

.is-dark-chrome-theme {
  @apply bg-off-black text-white;

  .VisionLink {
    @apply text-white;
  }
}
</style>
